import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCheck, Layers, Loader2, Play, Settings } from 'lucide-react'
import {
  useDecideTierReviewItem,
  useGetTierReview,
  useRunTierReview,
} from '../../hooks/backend/tierReview'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Switch } from '../../lib/shadcn/switch'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../../lib/shadcn/table'
import { toast } from '../../lib/shadcn/sonner'
import { HelpText } from '../../components/HelpText'
import { PRODUCTS_CHANGED } from '../../utils/hubSearchEvents'
import { TIER_REVIEW_CHANGED, type TierReviewData, type TierReviewItem } from '../../utils/tierReview'
import TierReviewRow, { type TierDecision } from './TierReviewRow'

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e))

// Products > Tier review: each quarter, every flavor's suggested wholesale
// tier from the previous quarter's sales, for Jade to approve or skip.
// Approving sets the tier on the flavor's sizes and syncs it to B2B.
export default function TierReviewPanel() {
  const getReview = useGetTierReview()
  const runReview = useRunTierReview()
  const decide = useDecideTierReviewItem()

  const [reviewId, setReviewId] = useState<number | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [busyAction, setBusyAction] = useState<{ id: number; decision: TierDecision } | null>(null)
  const [approvingAll, setApprovingAll] = useState(false)

  const load = (id: number | null = reviewId, skipCache = true) =>
    getReview.trigger(id != null ? { review_id: id } : {}, { skipCache })

  useEffect(() => {
    void load(null, false)
    const refresh = () => void load()
    window.addEventListener(TIER_REVIEW_CHANGED, refresh)
    return () => window.removeEventListener(TIER_REVIEW_CHANGED, refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const data = getReview.data as TierReviewData | null | undefined
  const review = data?.review ?? null
  const items = data?.items ?? []
  const changes = useMemo(() => items.filter(i => i.is_change), [items])
  const pending = changes.filter(i => i.decision === 'pending')
  const shown = showAll ? items : changes
  const slowMovers = items.filter(i => i.metrics.slowing).length
  const dueMissing = data != null && data.due.review_id == null

  const notifyChanged = () => {
    window.dispatchEvent(new Event(TIER_REVIEW_CHANGED))
    window.dispatchEvent(new Event(PRODUCTS_CHANGED))
  }

  const run = async () => {
    const quarterStart = review && !dueMissing ? review.quarter_start : data?.due.quarter_start
    try {
      const res = (await runReview.trigger(quarterStart ? { quarter_start: quarterStart } : {}).result) as {
        review_id: number
        quarter: string
        changes?: number
      }
      toast.success(`${res.quarter} review ready: ${res.changes ?? 0} change${res.changes === 1 ? '' : 's'} suggested`)
      setReviewId(res.review_id)
      await load(res.review_id).result
      window.dispatchEvent(new Event(TIER_REVIEW_CHANGED))
    } catch (e) {
      toast.error(`Couldn't run the review: ${errorText(e)}`)
    }
  }

  const decideOne = async (item: TierReviewItem, decision: TierDecision, quiet = false) => {
    setBusyAction({ id: item.id, decision })
    try {
      const res = (await decide.trigger({ item_id: item.id, decision }).result) as {
        tier?: string
        sync_error: string | null
      }
      if (!quiet) {
        if (res.sync_error) toast.warning(`${item.flavor}: ${res.sync_error}`, { duration: 15000 })
        else if (decision === 'approve') toast.success(`${item.flavor} moved to ${res.tier} and synced to B2B`)
        else if (decision === 'skip') toast.success(`${item.flavor} skipped`)
        else toast.success(`${item.flavor} is back to pending`)
      }
      return res.sync_error ? `${item.flavor}: ${res.sync_error}` : null
    } catch (e) {
      if (!quiet) toast.error(`${item.flavor}: ${errorText(e)}`)
      return `${item.flavor}: ${errorText(e)}`
    } finally {
      setBusyAction(null)
    }
  }

  const handleDecide = async (item: TierReviewItem, decision: TierDecision) => {
    await decideOne(item, decision)
    await load().result
    notifyChanged()
  }

  // One at a time: each approval syncs to B2B.
  const approveAll = async () => {
    setApprovingAll(true)
    const id = toast.loading(`Approving ${pending.length} tier changes…`)
    const problems: string[] = []
    let done = 0
    for (const item of pending) {
      const problem = await decideOne(item, 'approve', true)
      if (problem) problems.push(problem)
      done++
      toast.loading(`Approving tier changes (${done} of ${pending.length})…`, { id })
    }
    await load().result
    notifyChanged()
    setApprovingAll(false)
    if (problems.length > 0) toast.warning(problems.join(' · '), { id, duration: 20000 })
    else toast.success(`${done} tier change${done === 1 ? '' : 's'} approved and synced to B2B`, { id })
  }

  const busy = busyAction != null || approvingAll || runReview.loading

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
        <div className="space-y-1.5">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Wholesale tier review
          </CardTitle>
          <CardDescription><HelpText>
            Runs on the first day of each quarter and suggests a tier for every flavor from the
            previous quarter&apos;s sales: units (Small + Large), units per week and how that&apos;s
            changing, separate orders, and how many retailers ordered it. Seasonal flavors and
            seasonings aren&apos;t reviewed. Approving sets the tier on every size and syncs it to
            B2B, which applies that tier&apos;s minimum.
          </HelpText></CardDescription>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0">
          <Button variant="outline" size="sm" asChild>
            <Link to="/settings?section=products&item=tier-review">
              <Settings className="w-4 h-4 mr-2" />
              Thresholds
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={() => void run()} disabled={busy || !data}>
            {runReview.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            {dueMissing ? `Run ${data?.due.label} review` : 'Run again'}
          </Button>
          {pending.length > 0 && (
            <Button size="sm" onClick={() => void approveAll()} disabled={busy}>
              <CheckCheck className="w-4 h-4 mr-2" />
              Approve all ({pending.length})
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {getReview.error && (
          <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {getReview.error}
          </div>
        )}

        {data && data.reviews.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="tier-review-quarter" className="text-sm text-muted-foreground">
                Quarter
              </Label>
              <NativeSelect
                id="tier-review-quarter"
                value={review ? String(review.id) : ''}
                onChange={e => {
                  const id = Number(e.target.value)
                  setReviewId(id)
                  void load(id)
                }}
                className="h-9 w-36"
              >
                {data.reviews.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="tier-review-all" checked={showAll} onCheckedChange={setShowAll} />
              <Label htmlFor="tier-review-all" className="cursor-pointer font-normal">
                Show all flavors
              </Label>
            </div>
            {review && (
              <p className="text-sm text-muted-foreground">
                {review.changes} change{review.changes === 1 ? '' : 's'} suggested · {review.pending} to
                decide · {review.approved} approved · {review.skipped} skipped
                {slowMovers > 0 && ` · ${slowMovers} slow mover${slowMovers === 1 ? '' : 's'}`} · run{' '}
                {new Date(review.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {review.run_type === 'auto'
                  ? ' automatically'
                  : review.created_by
                    ? ` by ${review.created_by.includes('@') ? review.created_by.split('@')[0] : review.created_by}`
                    : ''}
              </p>
            )}
          </div>
        )}

        {dueMissing && review && (
          <p className="text-sm text-muted-foreground">
            {data?.due.label} hasn&apos;t been reviewed yet. Click Run {data?.due.label} review.
          </p>
        )}

        {!data && getReview.loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : data && !review ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No tier reviews yet. Click Run {data.due.label} review to make the first one.
          </p>
        ) : shown.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Every flavor&apos;s sales match its tier. Nothing to change this quarter.
          </p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table data-mobile-layout="grid">
              <TableHeader>
                <TableRow>
                  <TableHead>Flavor</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right" data-mobile="key">Units</TableHead>
                  <TableHead className="text-right" data-mobile="key">Per week</TableHead>
                  <TableHead className="text-right" data-mobile="key">Orders</TableHead>
                  <TableHead className="text-right" data-mobile="key">Retailers</TableHead>
                  <TableHead>Why</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map(item => (
                  <TierReviewRow
                    key={item.id}
                    item={item}
                    busyDecision={busyAction?.id === item.id ? busyAction.decision : null}
                    disabled={busy}
                    onDecide={(i, d) => void handleDecide(i, d)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
